<script setup>
import { computed, toRefs } from 'vue';

import AddContentTagsMenu from '@/components/AddContentTagsMenu.vue';
import AddToActionMenu from '@/components/AddToActionMenu.vue';
import AddToSchedulerMenu from '@/components/AddToSchedulerMenu.vue';
import AddVisualTagsMenu from '@/components/AddVisualTagsMenu.vue';
import CampaignPostControls from '@/components/CampaignPostControls.vue';
import { canAccessContentTags } from '@/components/ContentTags/content-tag-utils';
import ExportToMenu from '@/components/ExportToMenu.vue';
import { toolTips } from '@/config';

const props = defineProps({
  selectedMedia: { type: Array, required: true },
  isCreatorMedia: { type: Boolean, default: false },
  showAddToAction: { type: Boolean, default: false },
  showAddContentTags: { type: Boolean, default: false },
  showAddVisualTags: { type: Boolean, default: false },
  showAddToScheduler: { type: Boolean, default: false },
  showExport: { type: Boolean, default: false },
  showExcludeInclude: { type: Boolean, default: false },
  campaignPostFilter: { type: String, default: '' },
  onPostsExcluded: { type: Function, default: null },
  onPostsIncluded: { type: Function, default: null },
});

const emit = defineEmits([
  'onActionComplete',
  'mediaAddedToCollection',
  'contentTagAdded',
  'visualTagAdded',
  'mediaScheduled',
  'mediaExported',
  'onPostsExcluded',
  'onPostsIncluded',
  'onActionStarted',
]);

const { selectedMedia } = toRefs(props);

const hasSelectedMedia = computed(() => selectedMedia.value.length > 0);

const hasSelectedDMedStories = computed(() =>
  selectedMedia.value.some((media) => media?.instagramStoryFrame?.importedViaDm),
);
</script>

<template>
  <div class="dash-flex dash-items-center dash-gap-2">
    <CampaignPostControls
      v-if="showExcludeInclude"
      :campaign-post-filter="campaignPostFilter"
      :selected-media="selectedMedia"
      :disabled="!hasSelectedMedia"
      :on-posts-excluded="onPostsExcluded"
      :on-posts-included="onPostsIncluded"
      @on-action-started="emit('onActionStarted')"
      @on-action-complete="emit('onActionComplete')"
      @on-posts-excluded="emit('onPostsExcluded')"
      @on-posts-included="emit('onPostsIncluded')"
    />
    <AddToActionMenu
      v-if="showAddToAction"
      :selected-media="selectedMedia"
      :is-creator-media="isCreatorMedia"
      :disabled="hasSelectedDMedStories || !hasSelectedMedia"
      :tooltip="hasSelectedDMedStories ? toolTips.cannotAddDMedStories : undefined"
      @on-action-complete="emit('onActionComplete')"
      @media-added-to-collection="emit('mediaAddedToCollection', $event)"
    />
    <AddContentTagsMenu
      v-if="props.showAddContentTags && canAccessContentTags()"
      :selected-media="selectedMedia"
      :disabled="!hasSelectedMedia"
      @on-action-complete="
        () => {
          emit('onActionComplete');
          emit('contentTagAdded');
        }
      "
    />
    <AddVisualTagsMenu
      v-if="props.showAddVisualTags"
      :selected-media="selectedMedia"
      :disabled="!hasSelectedMedia"
      @on-action-complete="
        () => {
          emit('onActionComplete');
          emit('visualTagAdded');
        }
      "
    />
    <AddToSchedulerMenu
      v-if="props.showAddToScheduler"
      :selected-media="selectedMedia"
      :disabled="hasSelectedDMedStories || !hasSelectedMedia"
      :tooltip="hasSelectedDMedStories ? toolTips.cannotScheduleDMedStories : undefined"
      @on-action-complete="emit('onActionComplete')"
      @media-scheduled="emit('mediaScheduled', $event)"
    />
    <ExportToMenu
      v-if="props.showExport"
      :selected-media="selectedMedia"
      :show-meta-ads="false"
      :show-download="false"
      @on-action-complete="
        (payload) => {
          emit('onActionComplete');
          emit('mediaExported', payload);
        }
      "
    />
    <slot />
  </div>
</template>
